import { taxonomyFor, type DrawingDiscipline } from '@/config/classification'
import { NodeType } from '@/types/graph'
import type { Node, SymbolNode, TagNode, ConnectionNode } from '@/service/graph'

/**
 * The node type a classification selection implies, or `null` when it already
 * matches `currentType` (so callers skip a no-op conversion).
 *
 * This is the decision the V1 detail/control panels apply when the user edits
 * the Category / Subcategory / Type dropdowns: the labeling-pass moved the tag
 * leaf to Annotation ▸ Text ▸ Tag (a Level III), so the node type can only be
 * resolved once the leaf is known. Centralizing it keeps both panels in step
 * and makes the conversion rule testable without the panel UI:
 *
 *   - picking Annotation ▸ Text ▸ Tag converts a symbol to a Tag node;
 *   - re-picking the Tag leaf (already a tag) is a no-op (returns null);
 *   - picking a non-tag leaf under Text (Note / Attribute / Numbering) does NOT
 *     keep it a tag — it resolves to a symbol, the same as any other leaf.
 */
export function nodeTypeChangeFor(
  levelI: string,
  levelII: string,
  levelIII: string | null,
  // The node's current type — a string so any node-type value (incl. ones the
  // panel never resolves to, e.g. 'table_cell') compares cleanly.
  currentType: string | undefined,
  // Which sheet `levelI` belongs to; defaults to P&ID.
  discipline: DrawingDiscipline = 'pnid',
): NodeType | null {
  const taxonomy = taxonomyFor(discipline)

  // Never convert off an INCOMPLETE classification when the kind is decided by
  // the leaf.
  //
  // The SLD sheet resolves `connection` from the leaf's geometry column, and its
  // `Line` group is MIXED — Feeder and Cable are lines, Line Crossing and Line
  // Break are boxes. So picking Level II `Line` and nothing else resolves to
  // `symbol`, and converting a drawn connection to a symbol clears its
  // `geometry_wkt` — the line's geometry is then gone on save. Wait for the leaf.
  //
  // P&ID is unaffected: it resolves at Level II (`Piping ▸ Line` is a connection
  // whatever leaf follows), so `resolvesNodeTypeFromLeaf` is false and this
  // guard never fires.
  if (taxonomy.resolvesNodeTypeFromLeaf && !levelIII?.trim()) return null

  const resolved = taxonomy.resolveNodeType(levelI, levelII, levelIII) as NodeType
  return resolved === currentType ? null : resolved
}

/**
 * `node` converted to `newType`, as the V1 detail panel applies it.
 *
 * Converting to a tag keeps any `content` the node already carries, so
 * Tag → Attribute → Tag does not erase the tag's text. A symbol has no
 * content, so it starts empty.
 */
export function convertNodeType(node: Node | undefined, newType: NodeType): Node {
  if (newType === NodeType.Symbol) return { ...node, type: NodeType.Symbol, name: '' } as SymbolNode
  if (newType === NodeType.Connection) {
    return { ...node, type: NodeType.Connection, geometry_wkt: undefined } as ConnectionNode
  }
  const content = (node as { content?: unknown } | undefined)?.content
  return {
    ...node,
    type: NodeType.Tag,
    content: typeof content === 'string' ? content : '',
  } as TagNode
}
